import TC from '../TC';
import Consts from './Consts';
import EventTarget from './EventTarget';
import i18n from './i18n';
TC.Consts = Consts;
TC.i18n = TC.i18n || i18n;

/**
  * Opciones básicas de control.
  * @typedef ControlOptions
  * @see MapControlOptions
  * @see 2-configuration
  * @property {HTMLElement|string} [div] - Elemento del DOM en el que crear el control o valor de atributo id de dicho elemento.
  */

TC.control = TC.control || {};

TC.Control = function () {
    const self = this;
    EventTarget.call(self);

    self.map = null;
    self.isActive = false;
    self.isDisabled = false;

    var len = arguments.length;

    self.options = TC.Util.extend({}, len > 1 ? arguments[1] : arguments[0]);
    self.id = self.options.id || TC.getUID({
        prefix: self.CLASS.substr(TC.Control.prototype.CLASS.length + 1) + '-'
    });
    self.div = TC.Util.getDiv(self.options.div ? self.options.div : arguments[0]);
    self.div.classList.add(TC.Control.prototype.CLASS, self.CLASS);

    self.template = self.options.template || self.template;
    self.exportsState = false;    
};

TC.inherit(TC.Control, EventTarget);

(function () {
    const ctlProto = TC.Control.prototype;

    ctlProto.CLASS = 'tc-ctl';

    ctlProto.template = void 0;

    ctlProto.show = function () {
        this.div.style.display = '';
    };

    ctlProto.hide = function () {
        this.div.style.display = 'none';
    };

    ctlProto.render = function (callback) {
        const self = this;
        return self._set1stRenderPromise(self.renderData(null, function () {
            self.addUIEventListeners();
            if (typeof callback === 'function') {
                callback();
            }
        }));
    };

    ctlProto._set1stRenderPromise = function (promise) {
        const self = this;
        if (!self._firstRender) {
            self._firstRender = promise;
        }
        return promise;
    };

    ctlProto.renderData = function (data, callback) {
        const self = this;
        return new Promise(function (resolve, _reject) {
            if (self.map) {
                self.trigger(TC.Consts.event.BEFORECONTROLRENDER, { dataObject: data });
            }
            self.div.classList.toggle(TC.Consts.classes.DISABLED, self.isDisabled);

            let template;
            if (typeof self.template === 'object' && !self.template.compiler) {
                template = self.template[self.CLASS];
            }
            else {
                template = self.template;
                self.template = {};
                self.template[self.CLASS] = template;
            }

            self.getRenderedHtml(self.CLASS, data)
                .then(function (html) {
                    self.div.innerHTML = html;
                    if (self.map) {
                        self.trigger(TC.Consts.event.CONTROLRENDER);
                    }
                    if (TC.Util.isFunction(callback)) {
                        callback();
                    }
                    resolve();
                });
        });
    };

    const processTemplates = function (templates, options) {
        options = options || {};
        return new Promise(function (resolve, _reject) {
            let mustCompile = false;
            for (var key in templates) {
                const template = templates[key];
                if (typeof template === 'string') {
                    mustCompile = true;
                }
            }

            const callback = function () {
                const templatePromises = [];
                for (var key in templates) {
                    const templateName = key;
                    let template = templates[templateName];
                    if (typeof template === 'string') {
                        templatePromises.push(new Promise(function (res, rej) {
                            TC.ajax({
                                url: template,
                                method: 'GET',
                                responseType: 'text'
                            })
                                .then(function (response) {
                                    templates[templateName] = template = TC._hbs.compile(response.data); // TODO: add optimization options
                                    res(template);
                                })
                                .catch(function (err) {
                                    console.log("Error fetching template: " + err);
                                    rej(err);
                                });
                        }));
                    }
                    else {
                        if (typeof template === 'object') {
                            templates[key] = template = TC._hbs.template(template);
                        }
                    }
                }

                Promise.all(templatePromises).then(function () {
                    for (var key in templates) {
                        const t = templates[key];
                        if (t && key !== options.className) {
                            TC._hbs.registerPartial(key, templates[key]);
                        }
                    }
                    resolve();
                });
            };

            if (mustCompile) {
                TC.loadJSInOrder(
                    !TC._hbs || !TC._hbs.compile,
                    [
                        TC.apiLocation + TC.Consts.url.TEMPLATING_FULL,
                        TC.apiLocation + TC.Consts.url.TEMPLATING_HELPERS
                    ],
                    callback
                );
            }
            else {
                TC.loadJSInOrder(
                    !TC._hbs,
                    [
                        TC.apiLocation + TC.Consts.url.TEMPLATING_RUNTIME,
                        TC.apiLocation + TC.Consts.url.TEMPLATING_HELPERS
                    ],
                    callback
                );
            }
        });
    };

    ctlProto.getRenderedHtml = function (templateId, data, callback) {
        const self = this;
        return new Promise(function (resolve, _reject) {

            const endFn = function (template) {
                if (typeof template === 'undefined') {
                    resolve('');
                    return;
                }
                const html = template(data);
                if (TC.Util.isFunction(callback)) {
                    callback(html);
                }
                resolve(html);
            };

            const template = self.template[templateId];
            if (typeof template !== 'function') {
                processTemplates(self.template, { locale: self.map && self.map.options.locale, className: self.CLASS }).then(function () {
                    endFn(self.template[templateId]);
                });
            }
            else {
                endFn(template);
            }
        });
    };

    ctlProto.register = function (map) {
        const self = this;
        return new Promise(function (resolve, _reject) {
            self.map = map;
            Promise.resolve(self.render()).then(function () {
                if (self.options.active) {
                    self.activate();
                }
                resolve(self);
            });
        });
    };

    ctlProto.activate = function () {
        const self = this;
        if (self.map && self.map.activeControl && self.map.activeControl !== self) {
            self.map.previousActiveControl = self.map.activeControl;
            self.map.activeControl.deactivate();
        }
        self.isActive = true;
        if (self.map) {
            self.map.activeControl = self;
            self.map.trigger(TC.Consts.event.CONTROLACTIVATE, { control: self });
            self.trigger(TC.Consts.event.CONTROLACTIVATE, { control: self });
        }
    };

    ctlProto.deactivate = function (stopChain) {
        if (arguments.length === 0) {
            stopChain = false;
        }

        const self = this;
        self.isActive = false;
        if (self.map) {
            self.map.activeControl = null;

            if (!stopChain) {
                //determinar cuál es el control predeterminado para reactivarlo
                //salvo que sea yo mismo, claro
                var nextControl = self.map.getDefaultControl();
                if (nextControl === self) nextControl = null;
                else if (self.map.previousActiveControl === self) // GLS: Validamos antes de activar que el control activo anterior sea distinto al control actual
                    nextControl = null;
                else if (!nextControl) {
                    nextControl = self.map.previousActiveControl;
                }

                if (nextControl)
                    nextControl.activate();
            }
            self.map.trigger(TC.Consts.event.CONTROLDEACTIVATE, { control: self });
            self.trigger(TC.Consts.event.CONTROLDEACTIVATE, { control: self });
        }
    };

    ctlProto.enable = function () {
        const self = this;
        self.isDisabled = false;
        if (self.div) {
            self.div.classList.remove(TC.Consts.classes.DISABLED);
            delete self.div.dataset.tcMessage;
        }
        if (self.containerControl && self.containerControl.onControlEnable) {
            self.containerControl.onControlEnable(self);
        }
    };

    ctlProto.disable = function (options) {
        const self = this;
        options = options || {};
        self.isDisabled = true;
        if (self.div) {
            self.div.classList.add(TC.Consts.classes.DISABLED);
            let message = self.getLocaleString('disabledControl');
            if (options.reason) {
                message = `${message} - ${options.reason}`;
            }
            self.div.dataset.tcMessage = message;
        }
        if (self.containerControl && self.containerControl.onControlDisable) {
            self.containerControl.onControlDisable(self);
        }
    };

    //ctlProto.remove = function () {
    //    const self = this;
    //    if (self.map) {
    //        if (self.isActive) {
    //            self.deactivate();
    //        }
    //        const idx = self.map.controls.indexOf(self);
    //        if (idx >= 0) {
    //            self.map.controls.splice(idx, 1);
    //        }
    //        self.map = null;
    //        if (self._dialogDiv) {
    //            self._dialogDiv.remove();
    //        }
    //        if (self.div) {
    //            self.div.remove();
    //        }
    //    }
    //};

    ctlProto.renderPromise = function () {
        const self = this;
        return self._firstRender || new Promise(function (resolve, _reject) {
            self.one(TC.Consts.event.CONTROLRENDER, function () {
                resolve(self);
            });
        });
    };

    ctlProto.addUIEventListener = function (selector, event, listener) {
        const self = this;
        const elements = selector ? self.div.querySelectorAll(selector) : [self.div];
        elements.forEach(function (elm) {
            elm.addEventListener(event, listener);
        });
    };

    ctlProto.addUIEventListeners = function () {
    };

    ctlProto.isExclusive = function () {
        return false;
    };

    ctlProto.getLocaleString = function (key, texts) {
        var self = this;
        var locale = self.map ? self.map.options.locale : TC.Cfg.locale;
        return TC.Util.getLocaleString(locale, key, texts);
    };

    ctlProto.getUID = function () {
        const self = this;
        return TC.getUID({
            prefix: self.id + '-'
        });
    };

    ctlProto.exportState = function () {
        const self = this;
        if (self.exportsState) {
            return {};
        }
        return null;
    };

    ctlProto.importState = function (_state) {
    };
    
    ctlProto.getDownloadDialog = function () {
        const self = this;
        self._downloadDialog = self._downloadDialog || self.map.getControlsByClass('TC.control.FeatureDownloadDialog')[0];
        if (self._downloadDialog) {
            self._downloadDialog.caller = self;
            return Promise.resolve(self._downloadDialog);
        }
        return new Promise(function (resolve, _reject) {
            self.map.addControl('FeatureDownloadDialog').then(ctl => {
                self._downloadDialog = ctl;
                self._downloadDialog.caller = self;
                resolve(ctl);
            });
        });
    };

    ctlProto.getElevationTool = function () {
        const self = this;
        if (!self.displayElevation && !self.options.displayElevation) {
            return Promise.resolve(null);
        }
        if (self.elevation) {
            return Promise.resolve(self.elevation);
        }
        return new Promise(function (resolve, _reject) {
            TC.loadJS(
                !TC.tool || !TC.tool.Elevation,
                TC.apiLocation + 'TC/tool/Elevation',
                function () {
                    if (typeof self.options.displayElevation === 'boolean') {
                        if (self.map) {
                            self.map.getElevationTool().then(function (mapElevation) {
                                if (mapElevation) {
                                    self.elevation = mapElevation;
                                }
                                else {
                                    self.elevation = new TC.tool.Elevation();
                                }
                                resolve(self.elevation);
                            });
                        }
                        else {
                            self.elevation = new TC.tool.Elevation();
                            resolve(self.elevation);
                        }
                    }
                    else {
                        if (self.map) {
                            self.map.getElevationTool().then(function (mapElevation) {
                                if (mapElevation) {
                                    self.elevation = new TC.tool.Elevation(TC.Util.extend(true, {}, mapElevation.options, self.options.displayElevation));
                                }
                                else {
                                    self.elevation = new TC.tool.Elevation(self.options.displayElevation);
                                }
                                resolve(self.elevation);
                            });
                        }
                        else {
                            self.elevation = new TC.tool.Elevation(self.options.displayElevation);
                            resolve(self.elevation);
                        }
                    }
                }
            );
        });
    };   

})();

TC.Control.create = async function (type, options) {
    const ctorName = type.substr(0, 1).toUpperCase() + type.substr(1);
    if (!TC.control[ctorName]) {
        const module = await import('./control/' + ctorName);
        TC.control[ctorName] = module.default;
    }
    return new TC.control[ctorName](null, options);
};

var Control = TC.Control;
export default Control;